import agwaLockHeldBannerStyles from './agwa-lock-held-banner.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '../user/username.js';
import { terms } from '../../language/terms';

/**
 * `agwa-lock-held-banner` - the one way a page tells the person that somebody ELSE holds the lock
 * on what they are looking at, and so why they cannot edit it (#398 C11, @LOCK-033).
 *
 * The architect, 2026-10-03: "lock works the same as other locks showing on the page, locked for
 * editing by xxxx". The Product Registry report and batch pages each rendered this sentence inline,
 * four copies of one alert; lifted here so the company page could say the same thing in the same
 * words, and so a wording change is one edit. Its counterpart for the lock THIS tab lost is
 * `agwa-lock-lost-banner`.
 *
 * The holder is named through the directory by id (D-151, contact rule 8), "another user" when the
 * page knows of a lock but not whose (a type that reports no holder). The lease, when known, is
 * the time the lock lapses if its holder walks away, which is when the reader may try again.
 *
 * The host decides whether to mount it (another person's live lock) and passes what it knows;
 * the banner owns only the wording.
 */
@customElement('agwa-lock-held-banner')
export class AgwaLockHeldBanner extends LitElement {
  static styles = unsafeCSS(agwaLockHeldBannerStyles);

  /** How the locked thing is named to the person, lower case: "report", "batch", or a term's value. */
  @property({ type: String }) noun = 'record';

  /** What the holder is doing under the lock: "editing", or "reviewing" for an auditor's review lock. */
  @property({ type: String }) verb = 'editing';

  /** Login id of the person holding the lock; null or empty when the page does not know who. */
  @property({ type: String, attribute: 'holder-user-id' })
  holderUserId: string | null = null;

  /** When the holder's lease lapses, if known. */
  @property({ attribute: false }) leaseExpiresAt: Date | null = null;

  render() {
    const who = this.holderUserId
      ? html`<user-name .userId=${this.holderUserId}></user-name>`
      : `another ${terms.loginUser.lower}`;
    return html`<sl-alert variant="warning" open>
      <sl-icon slot="icon" name="lock"></sl-icon>
      <p class="message">
        <strong>${who}</strong> is currently ${this.verb} this ${this.noun}.
        ${
          this.leaseExpiresAt
            ? html`Lease expires
                <sl-format-date
                  date=${this.leaseExpiresAt.toISOString()}
                  hour="numeric"
                  minute="2-digit"
                ></sl-format-date
                >.`
            : nothing
        }
      </p>
    </sl-alert>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-lock-held-banner': AgwaLockHeldBanner;
  }
}
